import { Injectable } from "@angular/core";
import { LogLevel } from "../utils/log-level.enum";
import { environment } from "../../environments/environment";

@Injectable({
	// The service will be available application wide as a singleton with no need to add it to a module's providers array
	// If the service is only used within a lazy loaded module it will be lazy loaded with that module
	providedIn: "root",
})
export class LoggerService {
	private static level: LogLevel = LogLevel.TRACE;

	public static showTrace(...params: any): void {
		LoggerService.writeToLog(LogLevel.TRACE, ...params);
	}

	public static showDebug(...params: any): void {
		LoggerService.writeToLog(LogLevel.DEBUG, ...params);
	}

	public static showInfo(...params: any) {
		LoggerService.writeToLog(LogLevel.INFO, ...params);
	}

	public static showWarn(...params: any) {
		LoggerService.writeToLog(LogLevel.WARN, ...params);
	}

	public static showError(...params: any) {
		LoggerService.writeToLog(LogLevel.ERROR, ...params);
	}

	public static showFatal(...params: any) {
		LoggerService.writeToLog(LogLevel.FATAL, ...params);
	}

	/**
	 * Check if it should write the log
	 * Check the logger level of the environment (local, dev, stage, prod) configs contain in src/environment folder
	 */
	private static shouldLog(level: LogLevel): boolean {
		return (
			(level >= environment.logger.level && level !== LogLevel.OFF) ||
			this.level === LogLevel.ALL
		);
	}

	/**
	 * Write the logs.
	 * IIFE Immediately Invoked Function Expression: msg and console.trace/log/info/warn/error will be discarded after the function is executed
	 */
	private static writeToLog(level: LogLevel, ...params: any) {
		if (this.shouldLog(level)) {
			switch (level) {
				case LogLevel.TRACE:
					(() => {
						const msg = `logger ${LogLevel[level]} \n`;
						console.trace(
							LoggerService.getLogDate(),
							msg,
							...params
						);
					})();
					break;
				case LogLevel.DEBUG:
					(() => {
						const msg = `logger ${LogLevel[level]} \n`;
						console.log(LoggerService.getLogDate(), msg, ...params);
					})();
					break;
				case LogLevel.INFO:
					(() => {
						const msg = `logger ${LogLevel[level]} \n`;
						console.info(
							LoggerService.getLogDate(),
							msg,
							...params
						);
					})();
					break;
				case LogLevel.WARN:
					(() => {
						const msg = `logger ${LogLevel[level]} \n`;
						console.warn(
							LoggerService.getLogDate(),
							msg,
							...params
						);
					})();
					break;
				case LogLevel.ERROR:
					(() => {
						const msg = `logger ${LogLevel[level]} \n`;
						console.error(
							LoggerService.getLogDate(),
							msg,
							...params
						);
					})();
					break;
			}
		}
	}

	/**
	 * To add the local date and time on logs.
	 */
	private static getLogDate(): string {
		const date = new Date();
		return (
			"[" +
			date.getDate() +
			"/" +
			(date.getMonth() + 1) +
			"/" +
			date.getFullYear() +
			" " +
			date.getHours() +
			":" +
			date.getMinutes() +
			":" +
			date.getSeconds() +
			"." +
			date.getMilliseconds() +
			"]"
		);
	}
}
